import React, { useState, useEffect, useRef } from 'react';
import { FiMapPin, FiChevronDown, FiX, FiNavigation } from 'react-icons/fi';

const CITY_COORDS = {
  'denver, co': { lat: 39.7392, lng: -104.9903 },
  'baton rouge, la': { lat: 30.4515, lng: -91.1871 },
  'bismarck, nd': { lat: 46.8084, lng: -100.7837 },
  'san antonio, tx': { lat: 29.4241, lng: -98.4936 },
  'colorado springs, co': { lat: 38.8339, lng: -104.8214 },
  'shreveport, la': { lat: 32.5252, lng: -93.7502 },
  'sacramento, ca': { lat: 38.5816, lng: -121.4944 },
  'sioux falls, sd': { lat: 43.5460, lng: -96.7313 },
  'albuquerque, nm': { lat: 35.0844, lng: -106.6504 },
  'houston, tx': { lat: 29.7604, lng: -95.3698 },
  'new orleans, la': { lat: 29.9511, lng: -90.0715 },
  'fort collins, co': { lat: 40.5853, lng: -105.0844 },
  'dallas, tx': { lat: 32.7767, lng: -96.7970 },
  'austin, tx': { lat: 30.2672, lng: -97.7431 },
  'nashville, tn': { lat: 36.1627, lng: -86.7816 },
  'oklahoma city, ok': { lat: 35.4676, lng: -97.5164 },
  'phoenix, az': { lat: 33.4484, lng: -112.0740 },
  'atlanta, ga': { lat: 33.7490, lng: -84.3880 },
  'columbus, oh': { lat: 39.9612, lng: -82.9988 },
  'indianapolis, in': { lat: 39.7684, lng: -86.1581 },
  'plano, tx': { lat: 33.0198, lng: -96.6989 },
  'fort worth, tx': { lat: 32.7555, lng: -97.3308 },
};

const DISTANCE_OPTIONS = [
  { value: 0, label: 'Any distance' },
  { value: 25, label: 'Within 25 miles' },
  { value: 50, label: 'Within 50 miles' },
  { value: 100, label: 'Within 100 miles' },
  { value: 200, label: 'Within 200 miles' },
  { value: 500, label: 'Within 500 miles' },
];

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getCoordsForLocation(locationStr) {
  if (!locationStr) return null;
  const key = locationStr.toLowerCase().trim();
  if (CITY_COORDS[key]) return CITY_COORDS[key];
  const match = Object.keys(CITY_COORDS).find(k => key.includes(k) || k.includes(key));
  return match ? CITY_COORDS[match] : null;
}

const LocationFilter = ({
  sheds,
  selectedLocation,
  onLocationChange,
  maxDistance,
  onMaxDistanceChange,
  userCoords,
  onUserCoordsChange,
  onRequestLocation,
  locationStatus,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  const uniqueLocations = React.useMemo(() => {
    const locs = new Map();
    sheds.forEach(shed => {
      if (shed.dealerLocation) {
        const key = shed.dealerLocation.toLowerCase().trim();
        if (!locs.has(key)) {
          const coords = getCoordsForLocation(shed.dealerLocation);
          let distance = null;
          if (userCoords && coords) {
            distance = Math.round(haversineDistance(userCoords.lat, userCoords.lng, coords.lat, coords.lng));
          }
          locs.set(key, {
            label: shed.dealerLocation,
            count: 1,
            coords,
            distance,
          });
        } else {
          locs.get(key).count++;
        }
      }
    });
    const arr = Array.from(locs.values());
    if (userCoords) {
      arr.sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999));
    } else {
      arr.sort((a, b) => a.label.localeCompare(b.label));
    }
    return arr;
  }, [sheds, userCoords]);

  const filtered = search
    ? uniqueLocations.filter(l => l.label.toLowerCase().includes(search.toLowerCase()))
    : uniqueLocations;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex gap-2 flex-wrap">
      {/* Location dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => { setIsOpen(!isOpen); setSearch(''); }}
          className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 hover:border-gray-300 transition-colors min-w-[200px]"
        >
          <FiMapPin className={selectedLocation ? 'text-gray-900' : 'text-gray-400'} />
          <span className={`flex-1 text-left text-sm ${selectedLocation ? 'text-gray-900' : 'text-gray-500'}`}>
            {selectedLocation || 'All Locations'}
          </span>
          {selectedLocation && (
            <button
              onClick={(e) => { e.stopPropagation(); onLocationChange(null); }}
              className="p-0.5 hover:bg-gray-100"
            >
              <FiX size={14} className="text-gray-400" />
            </button>
          )}
          <FiChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <div className="absolute top-full left-0 mt-1 w-full min-w-[280px] bg-white border border-gray-200 shadow-lg z-50 overflow-hidden">
              {/* Use my location button */}
              <button
                onClick={() => { onRequestLocation(); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 text-gray-700"
              >
                <FiNavigation className="w-4 h-4 text-blue-500" />
                <span className="font-medium">
                  {locationStatus === 'loading' ? 'Getting location...' :
                   locationStatus === 'denied' ? 'Location blocked — enable in browser' :
                   userCoords ? 'Using your location' : 'Use my location'}
                </span>
                {userCoords && <span className="ml-auto text-[11px] text-green-600">Active</span>}
              </button>

              {/* Search */}
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  placeholder="Search location..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gray-400"
                  autoFocus
                />
              </div>

              {/* All Locations */}
              <button
                onClick={() => { onLocationChange(null); setIsOpen(false); }}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${!selectedLocation ? 'bg-gray-50 font-medium' : ''}`}
              >
                <FiMapPin className="w-3.5 h-3.5 text-gray-400" />
                <span>All Locations</span>
                <span className="ml-auto text-xs text-gray-400">{sheds.length}</span>
              </button>

              <div className="border-t border-gray-100" />

              {/* Location list */}
              <div className="max-h-64 overflow-y-auto">
                {filtered.length > 0 ? (
                  filtered.map(loc => (
                    <button
                      key={loc.label}
                      onClick={() => { onLocationChange(loc.label); setIsOpen(false); }}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${selectedLocation === loc.label ? 'bg-gray-50 font-medium' : ''}`}
                    >
                      <FiMapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-left">{loc.label}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {loc.distance != null ? `${loc.distance} mi` : `${loc.count}`}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center text-sm text-gray-400">No locations found</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Distance filter — only show when user has shared location */}
      {userCoords && (
        <div className="relative">
          <select
            value={maxDistance}
            onChange={(e) => onMaxDistanceChange(Number(e.target.value))}
            className="appearance-none bg-white border border-gray-200 px-4 py-3 pr-9 text-sm focus:outline-none focus:border-gray-400 hover:border-gray-300 transition-colors"
          >
            {DISTANCE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
        </div>
      )}
    </div>
  );
};

export { haversineDistance, getCoordsForLocation, CITY_COORDS };
export default LocationFilter;
